import Table from '@/components/table/Table';
import { DISPPLAY_MOMENT_YMD_HM, DEFAULT_SELECT_PAGE_SIZES } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import { VehicleOverdueForArrangement } from '@/types/dashboard';
import { formatDateTime } from '@/utils/datetime';
import { getLinkAccountDetail, getLinkBikeDetail, getLinkLocationDetail, getLinkOrderDetail } from '@/utils/utils';
import { ColumnDef, TableState } from '@tanstack/react-table';
import { Updater } from '@tanstack/react-table';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export default observer(function InstallationExpired() {
    // hooks
    const { t } = useTranslation();

    // store
    const {
        dashboardStore: {
            getVehiclesOverdueForArrangement,
            vehiclesOverdueForArrangement,
            pagingVehiclesOverdueForArrangement,
            totalVehiclesOverdueForArrangement,
            cleanVehiclesToArrangeIn24h
        }
    } = useStore();

    useEffect(() => {
        getVehiclesOverdueForArrangement();

        return () => {
            cleanVehiclesToArrangeIn24h();
        };
    }, []);

    // columns
    const columns: ColumnDef<VehicleOverdueForArrangement>[] = [
        {
            accessorKey: 'orderCodeSystem',
            header: t('field.reservation_code'),
            size: 150,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <p className='min-w-[100px]'>
                    {original.orderCodeSystem?.length > 0 && (
                        <Link
                            to={getLinkOrderDetail(original.id)}
                            target='_blank'
                            className='link-normal'
                        >
                            {original.orderCodeSystem}
                        </Link>
                    )}
                </p>
            )
        },
        {
            accessorKey: 'location',
            header: t('field.location_name'),
            size: 150,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='break-all min-w-[100px] line-clamp-2'>
                    {original.location?.name?.length > 0 && (
                        <Link
                            to={getLinkLocationDetail(original.location.id)}
                            target='_blank'
                            className='link-normal'
                        >
                            {original.location?.name}
                        </Link>
                    )}
                </div>
            )
        },
        {
            accessorKey: 'preparationTime',
            header: t('field.installation_deadline'),
            size: 150,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='whitespace-nowrap'>
                    {formatDateTime(original.preparationTime, DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false })}
                </div>
            )
        },
        {
            accessorKey: 'rentalFrom',
            header: t('field.rental_date_and_time'),
            size: 150,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='whitespace-nowrap'>
                    {formatDateTime(original.rentalFrom, DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false })}
                </div>
            )
        },
        {
            accessorKey: 'rentalTo',
            header: t('field.return_date_and_time'),
            size: 150,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='whitespace-nowrap'>
                    {formatDateTime(original.rentalTo, DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false })}
                </div>
            )
        },
        {
            accessorKey: 'bike',
            header: t('field.model_bike'),
            size: 250,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='break-all min-w-[100px] line-clamp-2'>
                    {original.bike?.bikeModel?.length > 0 && (
                        <Link
                            to={getLinkBikeDetail(original.bike.id)}
                            target='_blank'
                            className='link-normal'
                        >
                            {original.bike?.bikeModel}
                        </Link>
                    )}
                </div>
            )
        },
        {
            accessorKey: 'renter',
            header: t('field.customer_name'),
            size: 100,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='break-all min-w-[90px] line-clamp-2'>
                    {original.renter?.fullName?.length > 0 && (
                        <Link
                            to={getLinkAccountDetail(original.renter.id)}
                            target='_blank'
                            className='link-normal'
                        >
                            {original.renter?.fullName}
                        </Link>
                    )}
                </div>
            )
        },
        {
            accessorKey: 'owner',
            header: t('field.owner'),
            size: 100,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='break-all min-w-[90px] line-clamp-2'>
                    {original.owner?.fullName?.length > 0 && (
                        <Link
                            to={getLinkAccountDetail(original.owner.id)}
                            target='_blank'
                            className='link-normal'
                        >
                            {original.owner?.fullName}
                        </Link>
                    )}
                </div>
            )
        }
    ];

    // functions
    const onStateChange = (stateUpdater: Updater<TableState>) => {
        const newPaging =
            stateUpdater instanceof Function ?
                stateUpdater(pagingVehiclesOverdueForArrangement) :
                stateUpdater;
        getVehiclesOverdueForArrangement({}, newPaging);
    };

    return (
        <div className='layout-card'>
            <Table
                columns={columns}
                data={vehiclesOverdueForArrangement}
                state={pagingVehiclesOverdueForArrangement}
                rowCount={totalVehiclesOverdueForArrangement}
                onStateChange={onStateChange}
                selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                getRowId={row => row.id?.toString()}
                showSelectPageSize={false}
            />
        </div>
    );
});
